/**
 * Gift Cards Module
 *
 * Handles gift card functionality including:
 * - Form summary/preview updates
 * - Template management
 * - Product management
 */

import { $, $$, ready } from '../utils/dom.js';

// =============================================================================
// Gift Card Form Summary
// =============================================================================

/**
 * Initialize gift card form summary updates
 */
function initGiftCardFormSummary() {
    const form = $('#giftcard-form');
    if (!form) return;

    const initialBalanceInput = $('#initial_balance');
    const cardTypeRadios = $$('input[name="card_type"]');
    const recipientNameInput = $('#recipient_name');
    const recipientEmailInput = $('#recipient_email');
    const expiresAtInput = $('#expires_at');

    function updateSummary() {
        // Update card type
        const cardTypeEl = document.querySelector('input[name="card_type"]:checked');
        const cardType = cardTypeEl ? cardTypeEl.value : 'digital';
        const summaryType = $('#summary-type');
        if (summaryType) {
            summaryType.textContent = cardType.charAt(0).toUpperCase() + cardType.slice(1);
        }

        // Update balance
        const balance = parseFloat(initialBalanceInput?.value) || 0;
        const summaryBalance = $('#summary-balance');
        const summaryTotal = $('#summary-total');
        if (summaryBalance) summaryBalance.textContent = '$' + balance.toFixed(2);
        if (summaryTotal) summaryTotal.textContent = '$' + balance.toFixed(2);

        // Update recipient name
        const previewName = $('#preview-name');
        if (previewName) {
            previewName.textContent = recipientNameInput?.value || 'Not specified';
        }

        // Update recipient email
        const previewEmail = $('#preview-email');
        if (previewEmail) {
            previewEmail.textContent = recipientEmailInput?.value || 'Not specified';
        }

        // Update expiry date
        const previewExpiry = $('#preview-expiry');
        if (previewExpiry) {
            if (expiresAtInput?.value) {
                const date = new Date(expiresAtInput.value);
                previewExpiry.textContent = date.toLocaleDateString();
            } else {
                previewExpiry.textContent = 'No expiration';
            }
        }
    }

    // Event listeners
    if (initialBalanceInput) {
        initialBalanceInput.addEventListener('input', updateSummary);
    }
    cardTypeRadios.forEach(radio => radio.addEventListener('change', updateSummary));
    if (recipientNameInput) {
        recipientNameInput.addEventListener('input', updateSummary);
    }
    if (recipientEmailInput) {
        recipientEmailInput.addEventListener('input', updateSummary);
    }
    if (expiresAtInput) {
        expiresAtInput.addEventListener('change', updateSummary);
    }

    // Initial update
    updateSummary();
}

// =============================================================================
// Gift Card Template Designer
// =============================================================================

/**
 * Initialize template designer preview
 */
function initTemplateDesigner() {
    const form = $('#template-form');
    if (!form) return;

    const nameInput = $('#name');
    const bgColorInput = $('#background_color');
    const textColorInput = $('#text_color');
    const messageInput = $('#default_message');

    function updatePreview() {
        const previewCard = $('#template-preview-card');
        if (!previewCard) return;

        // Update background color
        if (bgColorInput) {
            previewCard.style.backgroundColor = bgColorInput.value;
        }

        // Update text color
        if (textColorInput) {
            previewCard.style.color = textColorInput.value;
        }

        // Update preview name
        const previewName = $('#preview-template-name');
        if (previewName && nameInput) {
            previewName.textContent = nameInput.value || 'Gift Card';
        }

        // Update preview message
        const previewMessage = $('#preview-message');
        if (previewMessage && messageInput) {
            previewMessage.textContent = messageInput.value || 'Your gift awaits!';
        }
    }

    // Event listeners
    [nameInput, bgColorInput, textColorInput, messageInput].forEach(input => {
        if (input) {
            input.addEventListener('input', updatePreview);
            input.addEventListener('change', updatePreview);
        }
    });

    // Initial update
    updatePreview();
}

// =============================================================================
// Gift Card Product Form
// =============================================================================

/**
 * Initialize gift card product form
 */
function initGiftCardProductForm() {
    const form = $('#giftcard-product-form');
    if (!form) return;

    const fixedAmountRadio = $('#amount_type_fixed');
    const variableAmountRadio = $('#amount_type_variable');
    const fixedAmountSection = $('#fixed-amount-section');
    const variableAmountSection = $('#variable-amount-section');

    function toggleAmountSection() {
        if (!fixedAmountSection || !variableAmountSection) return;

        if (fixedAmountRadio?.checked) {
            fixedAmountSection.classList.remove('hidden');
            variableAmountSection.classList.add('hidden');
        } else if (variableAmountRadio?.checked) {
            fixedAmountSection.classList.add('hidden');
            variableAmountSection.classList.remove('hidden');
        }
    }

    if (fixedAmountRadio) {
        fixedAmountRadio.addEventListener('change', toggleAmountSection);
    }
    if (variableAmountRadio) {
        variableAmountRadio.addEventListener('change', toggleAmountSection);
    }

    // Initial toggle
    toggleAmountSection();
}

// =============================================================================
// Auto-Initialization
// =============================================================================

function initGiftCards() {
    initGiftCardFormSummary();
    initTemplateDesigner();
    initGiftCardProductForm();
}

ready(initGiftCards);

export default {
    initGiftCardFormSummary,
    initTemplateDesigner,
    initGiftCardProductForm
};
